import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import { usePaketeValidation } from "../../Components/Validation/use_Pakete";
import { useToast } from "../../Hooks/useToast";
import { UpdatePackegesApi } from "../../Apis/Package/Package";
import { fetchGetAllPackagesByIdApi } from "../../Redux/Package_AutoComplete";
import { useSelector, useDispatch } from "react-redux";
import imageCompression from 'browser-image-compression'; 

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

import FormikAutocompleteChips from "../../Components/Utils/Formik_Autocomplete";
import AlternativeHeader from "../../Components/Headres/Alternative_Header";
import FromikTextField from "../../Components/Utils/Formik_Texfiled";
import CustomButton from "../../Components/Utils/Custom_Btn";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const VisuallyHiddenInput = (props) => (
  <input style={{ display: "none" }} {...props} />
);

const Updatepackage = () => {
  const param = useParams();
  const data = useSelector((state) => state.PackegesAutocomplete);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const { toastSuccess, toastError } = useToast();

  const navigate = useNavigate();
  const { formValidation } = usePaketeValidation();

  // Handle image upload and compression
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        toastError('Nur Bilddateien sind erlaubt!');
        return;
      }

      try {
        // Compress the image
        const options = {
          maxSizeMB: 1, // 1 MB
          maxWidthOrHeight: 1024, // 1024px max width or height
          useWebWorker: true
        };
        const compressedFile = await imageCompression(file, options);

        // Set the image preview and file
        setSelectedImage(compressedFile);
        setImagePreview(URL.createObjectURL(compressedFile));
      } catch (error) {
        console.error("Error during image compression:", error);
        toastError("Fehler beim Bild hochladen");
      }
    }
  };

  useEffect(() => {
    if (param.id) {
      dispatch(fetchGetAllPackagesByIdApi(param.id));
    }
  }, [param.id]);

  useEffect(() => {
    const item = data?.data?.data;
    if (param.id && item) {
      formik.setValues({
        name: item.name || "",
        price: item.price || 60,
        duration: item.duration || "",
        longDescription: item.longDescription || "",
        servicesList: item.servicesList || [],
        photo: item.photo || "",
      });
      if (item.photo) {
        setImagePreview(item.photo);
      }
    }
  }, [param.id, data?.data?.data]);

  const formik = useFormik({
    initialValues: {
      name: "",
      price: 60,
      duration: "",
      servicesList: [],
      longDescription: "",
      photo: ""
    },
    onSubmit: async (values) => {
      try {
        if (param.id) {
          setLoading(true);

          const formData = new FormData();
          formData.append("name", values.name);
          formData.append("longDescription", values.longDescription);
          formData.append("price", values.price);
          formData.append("duration", values.duration);

          values.servicesList.forEach((service, index) => {
            formData.append(`servicesList[${index}]`, service);
          });

          if (selectedImage) {
            formData.append("image", selectedImage);
          }

          try {
            const response = await UpdatePackegesApi(formData, param.id);

            if (response.status === 200) {
              toastSuccess("Das Paket wurde erfolgreich aktualisiert!");
              navigate('/Pakete');
            } else {
              toastError("Die Aktualisierung des Pakets ist fehlgeschlagen.");
            }
          } catch (error) {
            console.error("Update error:", error);
            toastError("Ein Fehler ist beim Aktualisieren des Pakets aufgetreten.");
          } finally {
            setLoading(false);
          }
        }
      } catch (error) {
        console.error("Form submission error:", error);
        setLoading(false);
      }
    }
  });

  return (
    <Box ml={2} component="form" onSubmit={formik.handleSubmit}>
      <AlternativeHeader
        onPrevious={() => navigate("/Pakete")}
        title="Paket aktualisieren"
      />

      <Box mt={6}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
          <FromikTextField
            formik={formik}
            name="price"
            label="Preis (€)"
            fullWidth
          />
          </Grid>
          {/* unified pricing: removed separate holiday price field */}
          <Grid item xs={12} md={4}>
            <FromikTextField
              formik={formik}
              name="duration"
              label="Dauer"
              disabled
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FromikTextField
              formik={formik}
              name="name"
              label="Paketname"
              placeholder="Paketname"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <FormikAutocompleteChips
              formik={formik}
              name="servicesList"
              label="Services"
              placeholder="Service hinzufügen"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                height: "200px",
                border: "2px solid #D6B981",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                borderRadius: "12px"
              }}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="preview"
                  style={{
                    width: "90%",
                    height: "90%",
                    objectFit: "cover"
                  }}
                />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No image uploaded
                </Typography>
              )}
            </Box>

            <Button
              sx={{ mt: 3, width: "100%" }}
              component="label"
              variant="contained"
              style={{ backgroundColor: "#D6B981" }}
              startIcon={<CloudUploadIcon />}
            >
              Bild hochladen
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Button>
          </Grid>

          <Grid item xs={12}>
            <FromikTextField
              formik={formik}
              name="longDescription"
              label="Beschreibung"
              placeholder="1-Notizen\n2-Notizen\n3-Notizen"
              multiline
              rows={6}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mt: 4, bgcolor: "#000000" }} />

      <Box mt={2} display="flex" justifyContent="flex-end">
        <CustomButton
          onNavigate={formik.handleSubmit}
          title="speichern"
          disabled={loading }
        />
      </Box>
    </Box>
  );
};

export default Updatepackage;
